<script setup lang="ts">
import { select, arc } from 'd3';
import { onMounted, ref, watch } from 'vue';
import { BENCHMARK_COLOUR_COMBOS } from './benchmark-colors';

const svgRef = ref<SVGElement | null>(null);

const transformScore = (score?: number) => {
  if (score == null) return 0;
  if (score > 0) return `+${score}`;
  return score.toString();
};

interface Props {
  index: number;
  title: string;
  tenantPrimaryMetricScore?: number;
  benchmarkPrimaryMetricScore?: number;
  percentile?: number;
}

const props = defineProps<Props>();
const getGaugeBenchmarkScoreId = () => `active-program-gauge-benchmark-score-${props.index}`;
const getGaugeTenantScoreId = () => `active-program-gauge-tenant-score-${props.index}`;
const getPercentileId = () => `active-program-gauge-percentile-${props.index}`;
const getGaugeTitleId = () => `active-program-gauge-title-${props.index}`;

// Static values
const benchmarkLabel = 'BENCHMARK';
const minValue = '-100';
const maxValue = '+100';
// Static subtitle based on percentile
const subtitle =
  props.percentile != null
    ? `Top ${Math.ceil((props.percentile * 100) / 10) * 10}% within Australia for Jul - Sep 2025`
    : 'No data found for selection';

const drawGauge = () => {
  const svg = select(svgRef.value);

  // Draw the gauge
  svg.selectAll('*').remove();

  // Background arc (gray) - made even thinner
  const backgroundGauge = arc()
    .innerRadius(90)
    .outerRadius(100)
    .cornerRadius(10)
    .startAngle(-Math.PI / 2) as any;

  svg
    .append('path')
    .datum({
      endAngle: Math.PI / 2,
    })
    .attr('fill', '#e5e7eb')
    .attr('d', backgroundGauge);

  // Foreground arc (orange/brown)
  if (props.benchmarkPrimaryMetricScore != null) {
    const foregroundGauge = arc()
      .innerRadius(90)
      .outerRadius(100)
      .cornerRadius(10)
      .startAngle(-Math.PI / 2) as any;

    // Calculate angle based on benchmark score
    const normalizedValue = (props.benchmarkPrimaryMetricScore + 100) / 200; // Convert to 0-1 range
    const endAngle = (Math.PI / 180) * (normalizedValue * 180 - 90);
    svg
      .append('path')
      .datum({
        endAngle: endAngle,
      })
      .attr('fill', BENCHMARK_COLOUR_COMBOS.ACTIVE_GAUGE.foreground)
      .attr('d', foregroundGauge);

    // Center score text
    svg
      .append('text')
      .text(transformScore(props.benchmarkPrimaryMetricScore))
      .attr('id', getGaugeBenchmarkScoreId())
      .attr('text-anchor', 'middle')
      .attr('transform', 'translate(0, -20)')
      .attr('font-size', '40')
      .attr('font-weight', 'bold')
      .attr('fill', '#4F4F4F');

    svg
      .append('text')
      .text(benchmarkLabel)
      .attr('text-anchor', 'middle')
      .attr('transform', 'translate(0, 5)')
      .attr('font-size', '13')
      .attr('fill', '#4F4F4F');
  } else {
    // Center score text
    svg
      .append('text')
      .text('-')
      .attr('text-anchor', 'middle')
      .attr('transform', 'translate(0, -20)')
      .attr('font-size', '40')
      .attr('font-weight', 'bold')
      .attr('fill', '#828282');

    svg
      .append('text')
      .text(benchmarkLabel)
      .attr('text-anchor', 'middle')
      .attr('transform', 'translate(0, 5)')
      .attr('font-size', '13')
      .attr('fill', '#828282');
  }

  // Min and max labels
  svg
    .append('text')
    .text(minValue)
    .attr('text-anchor', 'middle')
    .attr('transform', 'translate(-100, 25)')
    .attr('font-size', '12')
    .attr('font-weight', '400')
    .attr('fill', '#828282');

  svg
    .append('text')
    .text(maxValue)
    .attr('text-anchor', 'middle')
    .attr('transform', `translate(90, 25)`)
    .attr('font-size', '12')
    .attr('font-weight', '400')
    .attr('fill', '#828282');

  if (props.tenantPrimaryMetricScore != null) {
    // Calculate dot position based on tenantScore (one-time calculation)
    const clampedScore = Math.max(-100, Math.min(100, props.tenantPrimaryMetricScore));
    const angle = (clampedScore / 100) * (Math.PI / 2);
    const radius = 95;
    const dotX = radius * Math.sin(angle);
    const dotY = -radius * Math.cos(angle);

    // Add the dynamic dot
    svg
      .append('circle')
      .attr('r', '8px')
      .attr('fill', BENCHMARK_COLOUR_COMBOS.ACTIVE_GAUGE.dot)
      .attr('cx', dotX)
      .attr('cy', dotY);

    // Add the score box group
    const scoreGroup = svg.append('g');

    // Clamp the score box position to stay within viewBox boundaries (-130 to 130)
    // Score box is 112 units wide (56 units each side of center)
    const clampedDotX = Math.max(-85, Math.min(85, dotX));

    // Score box background rectangle
    scoreGroup
      .append('rect')
      .attr('x', clampedDotX - 45)
      .attr('y', dotY - 50)
      .attr('width', 90)
      .attr('height', 38)
      .attr('fill', BENCHMARK_COLOUR_COMBOS.ACTIVE_GAUGE.scoreBox)
      .attr('rx', 3);

    // Score box text - center on the clamped position
    scoreGroup
      .append('text')
      .attr('x', clampedDotX)
      .attr('y', dotY - 35)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11')
      .attr('font-weight', '500')
      .attr('fill', '#FFFFFF')
      .text(`YOUR SCORE:`);

    scoreGroup
      .append('text')
      .attr('x', clampedDotX)
      .attr('y', dotY - 19)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11')
      .attr('font-weight', '500')
      .attr('fill', '#FFFFFF')
      .attr('id', getGaugeTenantScoreId())
      .text(`${transformScore(props.tenantPrimaryMetricScore)}`);
  }
};

onMounted(() => {
  drawGauge();
});

// Watch for changes to props and redraw the gauge
watch(props, () => drawGauge(), { deep: true });
</script>

<template>
  <div class="bg-white rounded-xl px-2 py-6 w-100% m-0">
    <!-- Title -->
    <div class="text-center font-bold text-#4F4F4F text-2xl font-family[Poppins]" :id="getGaugeTitleId()">
      {{ title }}
    </div>

    <!-- Gauge SVG -->
    <svg viewBox="-130 -150 260 180">
      <g ref="svgRef" />
    </svg>

    <!-- Subtitle -->
    <div
      v-if="props.percentile != null"
      :id="getPercentileId()"
      class="text-center text-sm py-2 mt-0 mx-auto w-55% whitespace-pre-line text-center text-#16A34A"
    >
      {{ subtitle }}
    </div>

    <!-- Temporary Placeholder until percentile is finished -->
    <div
      v-else
      class="text-center text-sm py-2 mt-0 mx-auto w-55% whitespace-pre-line text-center text-#828282 italic mt-10"
    >
      <!-- {{ subtitle }} -->
    </div>
  </div>
</template>
